import textGuard from "et3-text-guard";
import { isDefinedString, reduce } from "et3-type-guards";
import { memoize } from "lodash";
import { computed, observable } from "mobx";
import { apiCallSignIn } from "../../../api/core-api/api-call-sign-in";
import { Ti18nKeys } from "../../../constants/i18n";
import { E_ADMIN_ROLE } from "../../../Types";
import { isValidPassword } from "../../../helpers/et3-data-helper";
import getCoreHistoryStore from "../core-history-store";
import getCoreTimingStore from "../core-timing-store";
import getCorePersonalStore from "../core-personal-store";
import getCoreUIStore from "../core-ui-store";
import getRootStore from "../root-store";
import getFirebaseStore from "../../firebase-store";
import { E_NOTIFICATION_PERMISSION } from "../../firebase-store/constants";
import getCorePopUpStore from "../core-popup-store";
import {
  DEFAULT_KHATTABAT_USER_LOGOUT,
  KHATTABA_POSSIBLE_ROLES,
  LOGIN_PAGE_LINK
} from "../../../constants/core-constants";
import getCoreNavigationStore from "../core-navigation-store";
import {
  E_LOCAL_STORAGE_KEY,
  E_STORAGE_TYPE
} from "../../../constants/storage-constants";
import getCoreStorageStore from "../core-storage-store";
import { apiCallSignOut } from "../../../api/core-api/api-call-sign-out";

class CoreLoginStore {
  username = observable.box<string>("");
  password = observable.box<string>("");

  errorKey = observable.box<Ti18nKeys>("EMPTY_STRING");
  hasSignInError = observable.box<boolean>(false);
  signInLoading = observable.box<boolean>(false);
  isSigningOut = observable.box<boolean>(false);
  isPasswordVisible = observable.box<boolean>(false);

  getIsCredentialsExist = computed(() => {
    const definedUserName = isDefinedString(this.username.get());
    const definedPassword = isDefinedString(this.password.get());

    return definedUserName && definedPassword;
  });

  isSignInDisabled = computed(() => {
    return !this.signInLoading.get() && !this.getIsCredentialsExist.get();
  });

  setError = (key: Ti18nKeys) => {
    this.errorKey.set(key);
    this.hasSignInError.set(key !== "EMPTY_STRING");
  };

  setPassword = (pass: string) => {
    this.setError("EMPTY_STRING");
    this.password.set(pass);
  };

  setUserName = (username: string) => {
    this.setError("EMPTY_STRING");
    this.username.set(username);
  };

  togglePasswordVisible = () => {
    this.isPasswordVisible.set(!this.isPasswordVisible.get());
  };

  submitSignIn = () => {
    this.signIn();
    getCoreNavigationStore().navigateToPage(LOGIN_PAGE_LINK, "");
  };

  signInOnEnter = (key: string) => {
    if (key === "Enter" && !this.isSignInDisabled.get()) {
      this.signIn();
    }
  };

  setSignInLoading = (isLoading: boolean) => {
    this.signInLoading.set(isLoading);
  };

  signIn = async () => {
    this.setSignInLoading(true);
    this.setError("EMPTY_STRING");
    const username = this.username.get();
    const password = this.password.get();
    const indexOfEt3Email = username.indexOf("@et3arraf.com");
    const cleanedUserName = username.replace("@et3arraf.com", "");
    const isValidUsername = textGuard.isAdminValidUserName({
      userName: cleanedUserName
    });

    if (
      !isValidPassword(password) ||
      !isValidUsername ||
      indexOfEt3Email === -1
    ) {
      this.setError("INVALID_CREDENTIALS");

      this.setSignInLoading(false);
      return;
    }

    const { data, status, code } = await apiCallSignIn({
      username,
      password
    });

    if (status === "OK") {
      const id = reduce(() => data.id, -1);
      const username = reduce(() => data.username, "");
      const fullName = reduce(() => data.fullName, "");
      const role = reduce(() => data.role, "");
      const lastLogin = reduce(() => data.lastLogin, "");
      const adminPermissions = reduce(() => data.adminPermissions, []);
      const needResetPassword = reduce(() => data.needResetPassword, false);

      // This admin client is restricted to Al-Khattaba (matchmaker) accounts.
      // Reject any other role even if the credentials are valid.
      const isKhattabaRole = KHATTABA_POSSIBLE_ROLES.includes(
        <E_ADMIN_ROLE>role
      );

      if (!isKhattabaRole) {
        this.setError("NO_PERMISSION");
        this.setSignInLoading(false);
        return;
      }

      getCorePersonalStore().setUser({
        id,
        username,
        fullName,
        role,
        lastLogin,
        needResetPassword
      });

      getCorePersonalStore().setUserPermission(adminPermissions);

      if (
        getFirebaseStore().refreshNotificationPermission() ===
        E_NOTIFICATION_PERMISSION.GRANTED
      ) {
        getFirebaseStore().registerIfNotificationPermissionGranted();
      } else {
        getCorePopUpStore().openNotificationPermissionPopUp();
      }

      const shouldContinue = await getRootStore().startApp();

      if (!shouldContinue) {
        return;
      }

      this.setSignInLoading(false);
      getCoreUIStore().setSideBarVisisble(true);
    } else if (status === "ERROR") {
      const error = reduce(() => code, "INVALID_CREDENTIALS");
      this.setError(error);

      this.setSignInLoading(false);
    }
  };

  signOutHandler = async () => {
    if (this.isSigningOut.get()) {
      return;
    }

    this.isSigningOut.set(true);
    try {
      await getCoreTimingStore().sendActiveTimeAndResetData();
      await getFirebaseStore().invalidateFirebaseToken();
      await apiCallSignOut();
      getRootStore().killAppTimersAndListenners();
      getCoreUIStore().isProfileDropdownVisible.set(false);
      getCorePersonalStore().user.set(DEFAULT_KHATTABAT_USER_LOGOUT);
      getCoreStorageStore(E_STORAGE_TYPE.LOCAL).remove(
        E_LOCAL_STORAGE_KEY.USER
      );
      getCoreStorageStore(E_STORAGE_TYPE.LOCAL).remove(
        E_LOCAL_STORAGE_KEY.USER_PERMISSIONS
      );
      getCoreStorageStore(E_STORAGE_TYPE.SESSION).clear();
      getCoreUIStore().isSideBarVisible.set(false);

      getCoreHistoryStore().setPageName("CORE_LOGIN", "CORE_LOGIN");
      getCoreUIStore().setIsLoading("CORE_LOGIN", false);
    } finally {
      this.isSigningOut.set(false);
    }
  };
}

const getCoreLoginStore = memoize(
  () => new CoreLoginStore(),
  () => 1
);

export default getCoreLoginStore;
